import { useEffect, useRef, useState } from 'react';

import { DownOutlined } from '@ant-design/icons';
import { cx } from '@emotion/css';
import { Flex } from '@revenuemarket/cds';
import { theme } from '@revenuemarket/design';
import { Condition } from '@revenuemarket/react';
import { ConversionPath, Flare, Typography } from '@revenuemarket/ui';
import { DropdownProps, Input, InputProps, InputRef, MenuProps } from 'antd';
import { debounce } from 'lodash-es';
import { match } from 'ts-pattern';

import {
  LabeledBankAccountTransaction,
  LabeledBy,
} from '~apis/cash-flow-overview/transactions/getTransactions';
import { CategoryTreeItem } from '~apis/categories';
import { LabeledCardBilling } from '~components/ui/AGGrid/editors/AGGridTransactionLabelSelectEditor';
import { FlattenTreeResult } from '~components/ui/HorizontalTreeTable/Table';
import { css } from '~libs/emotion';

export type LabelValue = string | null;

const debounceSearch = debounce((search: string, setSearch: (search: string) => void) => {
  setSearch(search);
}, 500);

export const LabelSelect = ({
  transaction,
  categories,
  value,

  open = false,
  inputProps,
}: {
  transaction?: LabeledBankAccountTransaction | LabeledCardBilling;
  categories: FlattenTreeResult<CategoryTreeItem>[];
  value?: LabelValue;
  onChange?: (key: string) => void;
  disable?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  menuProps?: Omit<MenuProps, 'selectable' | 'selectedKeys' | 'items' | 'onChange' | 'multiple'>;
  dropdownProps?: Pick<DropdownProps, 'trigger'>;
  inputProps?: Omit<InputProps, 'bordered' | 'value' | 'onChange' | 'onKeyDown'>;
  type?: 'CARD' | 'BANK_TRANSACTION';
  clearCellEvent?: () => void;
  hideSplitLabel?: boolean;
}) => {
  const inputRef = useRef<InputRef>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState('');

  const [selectValue, setSelectValue] = useState<string | undefined>(
    transaction?.isUnclassified || !transaction?.labelKey
      ? undefined
      : value || transaction.labelKey,
  );

  useEffect(() => {
    setSelectValue(
      transaction?.isUnclassified || !transaction?.labelKey ? undefined : transaction.labelKey,
    );
  }, [transaction]);

  const targetValue = categories.find((c) => {
    return c.value?.categoryLabelKey === (value || selectValue);
  });

  const [focus, setFocus] = useState(false);

  return (
    <Flex
      gap={8}
      justify="space-between"
      align="center"
      className={cx(
        css`
          height: 100%;
        `,
        search !== '' &&
          css`
            width: 100%;
          `,
      )}
    >
      <Flex
        className={css`
          position: absolute;
          right: 16px;
          left: 0;
          top: 0;
          bottom: 0;
          background-color: transparent;
        `}
      >
        <Input
          ref={inputRef}
          bordered={false}
          onChange={(e) => {
            if (!search) {
              setSearch(e.target.value);
            } else {
              debounceSearch(e.target.value, setSearch);
            }
          }}
          onKeyDown={(e) => {
            if (!focus) {
              setFocus(true);
            }
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              dropdownRef.current?.querySelector('[role="menu"]')?.dispatchEvent(
                new KeyboardEvent('keydown', {
                  ...e,
                  view: window,
                }),
              );
            }
          }}
          {...inputProps}
        />
      </Flex>
      <Condition
        expression={search === ''}
        then={() =>
          !open ? (
            <>
              <Condition
                expression={targetValue != null}
                then={() => {
                  return (
                    <Flex
                      gap={10}
                      align="center"
                      className={css`
                        height: 100%;
                      `}
                    >
                      {match(transaction?.labeledBy)
                        .with(LabeledBy.CompanyRule, () => <ConversionPath />)
                        .with(LabeledBy.SystemRule, () => <Flare />)
                        .otherwise(() => null)}
                      <Typography variant="body1" color={theme.colorTokens.textDefault}>
                        {targetValue?.value?.name}
                      </Typography>
                    </Flex>
                  );
                }}
                else={() => (
                  <Typography variant="body1" color={theme.colorTokens.textPlaceholder}>
                    분류를 선택해주세요
                  </Typography>
                )}
              />

              <DownOutlined
                style={{ color: theme.colorTokens.iconPlaceholder, width: 12, height: 12 }}
              />
            </>
          ) : (
            <div />
          )
        }
      />
    </Flex>
  );
};
